import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { publicApi } from '../lib/publicApi';
import { useTenant } from '../tenant/TenantContext';
import type { TenantBranding } from '@delta/domain';

export type { TenantBranding } from '@delta/domain';

export const tenantBrandingQueryKey = (tenantId: string) => ['public', 'tenant-branding', tenantId] as const;

export function useTenantBranding() {
  const tenant = useTenant();
  const query = useQuery({
    queryKey: tenantBrandingQueryKey(tenant.id),
    queryFn: async ({ signal }) => (
      await publicApi.get<TenantBranding>('/public/tenant/branding', { signal })
    ).data,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return {
    ...query,
    branding: query.data ?? {
      tenantId: tenant.id,
      displayName: tenant.name,
      logoAvailable: false,
      logoUrl: null,
      faviconAvailable: false,
      faviconUrl: null,
      version: 0,
      updatedAt: null,
    },
  };
}

interface BrandMarkProps {
  className: string;
  displayName: string;
  logoUrl: string | null;
}

export function BrandMark({ className, displayName, logoUrl }: BrandMarkProps) {
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showImage = Boolean(logoUrl && failedLogoUrl !== logoUrl);

  return (
    <span className={`${className}${showImage ? ' has-image' : ''}`} aria-hidden={!showImage}>
      {showImage
        ? <img src={logoUrl ?? undefined} alt={`${displayName} Logo`} onError={() => setFailedLogoUrl(logoUrl)} />
        : 'Δ'}
    </span>
  );
}
